import React from "react";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { Inline, Stack } from "components/layout/reusableLayouts";
import BalanceAmount from "components/common/BalanceAmount";
import TrendChart, { TrendPoint } from "components/common/TrendChart";

interface TrendPanelProps {
  title: string;
  /** The headline figure, already formatted. Its cents are raised. */
  value: string;
  valueLoading?: boolean;
  /** The window the chart covers. */
  caption: string;
  /** Beside the caption, such as the money in and out over the window. */
  aside?: React.ReactNode;
  series: TrendPoint[];
  formatValue: (value: number) => string;
  loading?: boolean;
}

/** A headline figure over its trend, the layout of the prototype's balance card. */
const TrendPanel: React.FC<TrendPanelProps> = ({
  title,
  value,
  valueLoading = false,
  caption,
  aside,
  series,
  formatValue,
  loading = false,
}) => (
  <Card
    variant="outlined"
    sx={{
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: "100%",
      padding: "var(--space-6)",
      gap: "var(--space-4)",
    }}
  >
    <Stack gap="var(--space-3)">
      <Typography variant="h4" component="h3">
        {title}
      </Typography>
      {valueLoading ? (
        <Typography variant="h2">...</Typography>
      ) : (
        <BalanceAmount
          value={value}
          variant="h2"
          centsSize="var(--type-size-lg)"
        />
      )}
    </Stack>

    <Inline justify="between" sx={{ alignItems: "center" }}>
      <Typography variant="label" sx={{ color: "var(--color-text-tertiary)" }}>
        {caption}
      </Typography>
      {aside}
    </Inline>

    <TrendChart series={series} formatValue={formatValue} loading={loading} />
  </Card>
);

export default TrendPanel;
